import { useEffect, useRef, useState } from "react";
import ui from "../components/ui.module.css";

// Adult-facing. The tablet is set up by scanning a one-time code that a
// teacher makes in the Teacher App. Nobody signs in here, and the tablet never
// holds an adult's credentials.

// A setup code arrives as a link to this app carrying `?setup=`.
export const setupCodeFrom = (text) => {
  try {
    const code = new URL(text).searchParams.get("setup");
    return code && /^[A-Za-z0-9_-]{16,128}$/.test(code) ? code : null;
  } catch {
    return null;
  }
};

const canScan = () =>
  typeof window !== "undefined" &&
  "BarcodeDetector" in window &&
  Boolean(navigator.mediaDevices?.getUserMedia);

// Looks for a QR code in the camera picture a few times a second.
const Scanner = ({ onCode, onCancel }) => {
  const videoRef = useRef(null);
  const [problem, setProblem] = useState(null);

  useEffect(() => {
    let stream;
    let timer;
    let stopped = false;
    const detector = new window.BarcodeDetector({ formats: ["qr_code"] });

    const look = async () => {
      if (stopped) return;
      try {
        const found = await detector.detect(videoRef.current);
        for (const { rawValue } of found) {
          const code = setupCodeFrom(rawValue);
          if (code) {
            onCode(code);
            return;
          }
          setProblem("That isn't a Thuto Time setup code.");
        }
      } catch {
        // The picture was not ready yet; look again.
      }
      timer = setTimeout(look, 300);
    };

    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: "environment" }, audio: false })
      .then((s) => {
        stream = s;
        if (stopped) return undefined;
        videoRef.current.srcObject = s;
        return videoRef.current.play().then(look);
      })
      .catch(() =>
        setProblem("The camera can't be used. Allow the camera, or use the tablet's camera app.")
      );

    return () => {
      stopped = true;
      clearTimeout(timer);
      stream?.getTracks().forEach((track) => track.stop());
    };
  }, [onCode]);

  return (
    <div className={ui.stack}>
      <video
        ref={videoRef}
        muted
        playsInline
        aria-label="Camera"
        style={{ width: "100%", borderRadius: 10, background: "#000", maxHeight: "50vh" }}
      />
      {problem && (
        <p className={ui.error} role="alert">
          {problem}
        </p>
      )}
      <button type="button" className={`${ui.button} ${ui.secondary}`} onClick={onCancel}>
        Cancel
      </button>
    </div>
  );
};

const Setup = ({ error, onCode }) => {
  const [scanning, setScanning] = useState(false);

  return (
    <main className={`${ui.page} ${ui.narrow}`}>
      <img
        src={`${process.env.PUBLIC_URL}/thuto-login.svg`}
        alt="Thuto Time"
        style={{ maxWidth: "100%", maxHeight: "30vh", alignSelf: "center" }}
      />
      <div className={ui.stack}>
        <h1 className={ui.title}>Set up this tablet</h1>
        <p className={ui.meta}>
          For the adult setting up the tablet. Learners never sign in.
        </p>
      </div>

      <ol className={`${ui.card} ${ui.stack}`} style={{ margin: 0, paddingLeft: 40 }}>
        <li>
          In the Teacher App, choose <strong>Set up learner tablet</strong> and
          pick the learner.
        </li>
        <li>Scan the code it shows with this tablet.</li>
      </ol>

      {error && (
        <p className={ui.error} role="alert">
          {error}
        </p>
      )}

      {scanning ? (
        <Scanner onCode={onCode} onCancel={() => setScanning(false)} />
      ) : canScan() ? (
        <button
          type="button"
          className={`${ui.button} ${ui.primary} ${ui.full}`}
          onClick={() => setScanning(true)}
        >
          Scan setup code
        </button>
      ) : (
        <p className={ui.meta}>Scan the code with this tablet's camera app.</p>
      )}
    </main>
  );
};

export default Setup;
